import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useTheme } from "@/contexts/ThemeContext";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import dayjs from "dayjs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  HelpCircle,
  Plus,
  Search,
  Edit,
  Trash2,
  Calendar,
  RefreshCw,
  FileText,
} from "lucide-react";

interface FAQ {
  _id: string;
  question: string;
  answer: string;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse {
  prev: boolean;
  next: boolean;
  page: number;
  item_per_page: number;
  total_page: number;
  total_count: number;
  history: FAQ[];
}

export function FAQs() {
  const { currentTheme } = useTheme();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState<FAQ | null>(null);
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
  });

  // Load FAQs
  const loadFAQs = async (page = 1) => {
    setLoading(true);
    try {
      const response = await apiService.getAllFAQs({ page });
      if (response.type === 0) {
        const data = response.data as PaginatedResponse;
        setFaqs(data.history);
        setCurrentPage(data.page);
        setTotalPages(data.total_page);
        setTotalCount(data.total_count);
      } else {
        toast.error(response.message || "Failed to load FAQs");
      }
    } catch (error) {
      toast.error("Failed to load FAQs");
      console.error("Error loading FAQs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadFAQs();
  }, []);

  // Create FAQ
  const handleCreateFAQ = async () => {
    if (!formData.question || !formData.answer) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await apiService.createFAQ(formData);
      if (response.type === 0) {
        toast.success("FAQ created successfully");
        setIsCreateDialogOpen(false);
        setFormData({ question: "", answer: "" });
        void loadFAQs(currentPage);
      } else {
        toast.error(response.message || "Failed to create FAQ");
      }
    } catch (error) {
      toast.error("Failed to create FAQ");
      console.error("Error creating FAQ:", error);
    }
  };

  // Update FAQ
  const handleUpdateFAQ = async () => {
    if (!selectedFAQ || !formData.question || !formData.answer) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await apiService.updateFAQ(selectedFAQ._id, formData);
      if (response.type === 0) {
        toast.success("FAQ updated successfully");
        setIsEditDialogOpen(false);
        setSelectedFAQ(null);
        setFormData({ question: "", answer: "" });
        void loadFAQs(currentPage);
      } else {
        toast.error(response.message || "Failed to update FAQ");
      }
    } catch (error) {
      toast.error("Failed to update FAQ");
      console.error("Error updating FAQ:", error);
    }
  };

  // Delete FAQ
  const handleDeleteFAQ = async (faqId: string) => {
    if (!confirm("Are you sure you want to delete this FAQ?")) return;

    try {
      const response = await apiService.deleteFAQ(faqId);
      if (response.type === 0) {
        toast.success("FAQ deleted successfully");
        void loadFAQs(currentPage);
      } else {
        toast.error(response.message || "Failed to delete FAQ");
      }
    } catch (error) {
      toast.error("Failed to delete FAQ");
      console.error("Error deleting FAQ:", error);
    }
  };

  // Edit FAQ
  const handleEditFAQ = (faq: FAQ) => {
    setFormData({
      question: faq.question,
      answer: faq.answer,
    });
    setSelectedFAQ(faq);
    setIsEditDialogOpen(true);
  };

  // Filter FAQs
  const filteredFAQs = faqs.filter(
    faq =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return "Invalid date";
    return dayjs(dateString).format("MMM DD, YYYY");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">FAQ Management</h1>
          <p className="text-muted-foreground">
            Manage frequently asked questions and their answers
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => {
              void loadFAQs(currentPage);
            }}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: currentTheme.colors.primary }}>
                <Plus className="w-4 h-4 mr-2" />
                Add FAQ
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New FAQ</DialogTitle>
                <DialogDescription>Create a new frequently asked question</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="question">Question</Label>
                  <Input
                    id="question"
                    placeholder="Enter the question..."
                    value={formData.question}
                    onChange={e => {
                      setFormData(prev => ({ ...prev, question: e.target.value }));
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="answer">Answer</Label>
                  <Textarea
                    id="answer"
                    placeholder="Enter the answer..."
                    value={formData.answer}
                    onChange={e => {
                      setFormData(prev => ({ ...prev, answer: e.target.value }));
                    }}
                    rows={6}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreateDialogOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateFAQ}
                  style={{ backgroundColor: currentTheme.colors.primary }}
                >
                  Create FAQ
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search FAQs by question or answer..."
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                }}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAQs List */}
      <Card>
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
          <CardDescription>{totalCount} total FAQs</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading FAQs...
            </div>
          ) : filteredFAQs.length === 0 ? (
            <div className="text-center py-8">
              <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No FAQs found</p>
            </div>
          ) : (
            <>
              <Accordion type="single" collapsible className="space-y-4">
                {filteredFAQs.map((faq, index) => (
                  <AccordionItem
                    key={faq._id}
                    value={`item-${index}`}
                    className="border rounded-lg px-4"
                  >
                    <div className="flex items-center justify-between py-4">
                      <AccordionTrigger className="hover:no-underline flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <FileText className="w-4 h-4" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium">{faq.question}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <Calendar className="w-3 h-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">
                                {formatDate(faq.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <div className="flex items-center gap-1 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Edit"
                          onClick={e => {
                            e.stopPropagation();
                            handleEditFAQ(faq);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Delete"
                          className="text-red-600 hover:text-red-700"
                          onClick={e => {
                            e.stopPropagation();
                            void handleDeleteFAQ(faq._id);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <AccordionContent>
                      <div className="pt-4 pb-2">
                        <div className="prose prose-sm max-w-none">
                          <p className="text-muted-foreground whitespace-pre-wrap">{faq.answer}</p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <p className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, totalCount)}{" "}
                    of {totalCount} results
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void loadFAQs(currentPage - 1);
                      }}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void loadFAQs(currentPage + 1);
                      }}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit FAQ Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit FAQ</DialogTitle>
            <DialogDescription>Update the frequently asked question</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editQuestion">Question</Label>
              <Input
                id="editQuestion"
                placeholder="Enter the question..."
                value={formData.question}
                onChange={e => {
                  setFormData(prev => ({ ...prev, question: e.target.value }));
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editAnswer">Answer</Label>
              <Textarea
                id="editAnswer"
                placeholder="Enter the answer..."
                value={formData.answer}
                onChange={e => {
                  setFormData(prev => ({ ...prev, answer: e.target.value }));
                }}
                rows={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateFAQ}
              style={{ backgroundColor: currentTheme.colors.primary }}
            >
              Update FAQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
